import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { Flag, ChevronRight, Trophy } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";

const TYPE_LABEL = {
  men: "Men's Senior",
  women: "Women's Senior",
  u20: "Under-20",
  u17: "Under-17",
};

const TYPE_ORDER = ["men", "women", "u20", "u17"];

const NationalTeamsPage = () => {
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(`${API}/national-teams`);
        const sorted = [...res.data].sort(
          (a, b) => TYPE_ORDER.indexOf(a.type) - TYPE_ORDER.indexOf(b.type)
        );
        setTeams(sorted);
      } catch (err) {
        console.error("Failed to load national teams", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  return (
    <div className="min-h-screen bg-gray-50" data-testid="national-teams-page">
      {/* Hero */}
      <section className="bg-[#09090b] py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-8">
          <div className="inline-block px-3 py-1 bg-[#FFC72C] text-black text-xs font-bold uppercase tracking-widest mb-4">
            Antigua &amp; Barbuda
          </div>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold uppercase text-white mb-3">
            National Teams
          </h1>
          <p className="text-gray-400 text-base md:text-lg max-w-2xl">
            The Benna Boys, She Benna Boys and youth squads representing Antigua &amp; Barbuda on the international stage.
          </p>
        </div>
      </section>

      {/* Teams list */}
      <section className="py-12 md:py-16 px-4 md:px-8">
        <div className="max-w-6xl mx-auto space-y-4">
          {loading ? (
            [...Array(4)].map((_, i) => (
              <Skeleton key={i} className="h-28 w-full rounded-lg" />
            ))
          ) : teams.length === 0 ? (
            <div className="text-center py-16 text-gray-500" data-testid="national-teams-empty">
              <Flag className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No national teams available yet.</p>
            </div>
          ) : (
            teams.map((team) => (
              <Link
                key={team.team_id}
                to={`/national-teams/${team.team_id}`}
                data-testid={`national-team-card-${team.type}`}
                className="group flex items-center gap-4 md:gap-6 bg-white border border-gray-200 hover:border-[#FFC72C] hover:shadow-md rounded-lg p-4 md:p-5 transition"
              >
                {/* Badge */}
                <div className="flex-shrink-0 w-16 h-16 md:w-20 md:h-20 bg-[#09090b] rounded-lg flex items-center justify-center p-2">
                  {team.logo_url ? (
                    <img
                      src={team.logo_url}
                      alt={team.name}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <Flag className="w-8 h-8 text-[#FFC72C]" />
                  )}
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="text-xs font-bold uppercase tracking-widest text-[#FFC72C] bg-[#09090b] px-2 py-0.5 rounded">
                      {TYPE_LABEL[team.type] || team.type}
                    </span>
                    {team.fifa_ranking && (
                      <span className="text-xs text-gray-500 font-medium">
                        FIFA #{team.fifa_ranking}
                      </span>
                    )}
                  </div>
                  <h2 className="font-heading text-lg md:text-xl font-bold text-[#09090b] uppercase leading-tight truncate">
                    {team.name}
                  </h2>
                  {team.nickname && (
                    <p className="text-sm text-gray-600 mt-0.5 italic truncate">
                      "{team.nickname}"
                    </p>
                  )}
                </div>

                {/* Confederation + arrow */}
                <div className="hidden md:flex flex-col items-end gap-1 flex-shrink-0">
                  {team.confederation && (
                    <div className="flex items-center gap-1 text-xs text-gray-500 font-semibold uppercase tracking-wider">
                      <Trophy className="w-3.5 h-3.5" />
                      {team.confederation}
                    </div>
                  )}
                  <span className="text-xs text-gray-400 group-hover:text-[#FFC72C] transition">
                    View squad
                  </span>
                </div>

                <ChevronRight className="w-5 h-5 text-gray-300 group-hover:text-[#FFC72C] group-hover:translate-x-1 transition-transform flex-shrink-0" />
              </Link>
            ))
          )}
        </div>
      </section>
    </div>
  );
};

export default NationalTeamsPage;
